<template>
  <v-card flat class="rmr-table-shell gp-table-shell">
    <rmr-table-header
      heading-class="gp-table-shell__heading"
      controls-class="gp-table-shell__controls"
    >
      <template #heading>
        <div class="gp-table-shell__title-row">
          <div class="rmr-table-shell__title">Marques disponibles</div>
          <span class="gp-table-shell__count-badge">{{ brands.length }}</span>
        </div>
      </template>
      <template #controls>
        <div class="rmr-table-shell__filter-item rmr-table-shell__search gp-table-shell__search">
          <label class="rmr-table-shell__filter-label">Recherche</label>
          <rmr-text-field
            :value="search"
            append-icon="mdi-magnify"
            label="Rechercher une marque"
            single-line
            @input="$emit('update:search', $event)"
          />
        </div>
        <rmr-action-button
          label="Historique"
          :icon="icons.mdiHistory"
          variant="primary"
          @click="$emit('show-history')"
        />
      </template>
    </rmr-table-header>

    <v-card-text class="rmr-table-shell__body">
      <v-data-table
        :headers="headers"
        :items="brands"
        :search="search"
        :loading="loading"
        class="rmr-data-table gp-table"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      >
        <template v-slot:loading>
          <div class="rmr-data-table__state">
            <v-progress-circular indeterminate color="#6366F1" size="28" class="mb-2" />
            <div class="rmr-data-table__state-title">Chargement des marques</div>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="rmr-data-table__state">
            <v-icon color="#94A3B8" size="32" class="mb-2">{{ icons.mdiTag }}</v-icon>
            <div class="rmr-data-table__state-title">
              {{ hasActiveFilters ? "Aucun résultat" : "Aucune marque disponible" }}
            </div>
            <div class="rmr-data-table__state-text">
              {{
                hasActiveFilters
                  ? "Aucune marque ne correspond à la recherche actuelle. Modifiez le terme recherché."
                  : "Aucune marque avec rapport associé n'a été trouvée."
              }}
            </div>
          </div>
        </template>

        <template v-slot:no-results>
          <div class="rmr-data-table__state">
            <v-icon color="#94A3B8" size="32" class="mb-2">{{ icons.mdiTag }}</v-icon>
            <div class="rmr-data-table__state-title">Aucun résultat</div>
            <div class="rmr-data-table__state-text">
              Aucune marque ne correspond à la recherche actuelle. Modifiez le terme recherché.
            </div>
          </div>
        </template>

        <template slot="item.brand_name" slot-scope="{ item }">
          <span class="gp-table__brand-name">{{ item.brand_name }}</span>
        </template>

        <template slot="item.template_number" slot-scope="{ item }">
          <span v-if="item.template_number" class="gp-table__badge">Template {{ item.template_number }}</span>
          <span v-else class="gp-table__empty">—</span>
        </template>

        <template slot="item.images" slot-scope="{ item }">
          <div class="gp-table__chips">
            <span v-if="item.author_image_path" class="gp-image-chip gp-image-chip--author">Auteur</span>
            <span v-if="item.presentation_image_path" class="gp-image-chip gp-image-chip--presentation">Présentation</span>
            <span v-if="item.social_image_path" class="gp-image-chip gp-image-chip--social">Social</span>
            <span v-if="!item.author_image_path && !item.presentation_image_path && !item.social_image_path" class="gp-table__empty">—</span>
          </div>
        </template>

        <template slot="item.report" slot-scope="{ item }">
          <div class="gp-table__report">
            <rmr-status-chip
              v-if="item.has_report_data"
              label="Prêt"
              variant="success"
              size="sm"
            />
            <v-tooltip v-else bottom>
              <template #activator="{ on, attrs }">
                <v-chip
                  small
                  class="gp-table__report-chip"
                  :style="missingReportChipStyle"
                  v-bind="attrs"
                  v-on="on"
                >
                  Manquant
                </v-chip>
              </template>
              <span>{{ getMissingReportTooltip(item) }}</span>
            </v-tooltip>
          </div>
        </template>

        <template slot="item.actions" slot-scope="{ item }">
          <div class="rmr-data-table__actions gp-table__actions">
            <rmr-action-menu
              trigger-mode="button"
              trigger-label="Options PPT"
              :trigger-icon="icons.mdiFilePowerpoint"
              trigger-button-variant="transparent"
              trigger-button-color="#F59E0B"
              :trigger-disabled="isPresentationProcessingBusy"
              size="xs"
              class="gp-table__action-button gp-table__action-button--ppt"
              :actions="getPptActions(item)"
              @select="runPptAction($event, item)"
            />

            <rmr-action-menu
              trigger-mode="button"
              trigger-label="Options PDF"
              :trigger-icon="icons.mdiFilePdfBox"
              trigger-button-variant="transparent"
              trigger-button-color="#DC2626"
              :trigger-disabled="isPresentationProcessingBusy"
              size="xs"
              class="gp-table__action-button gp-table__action-button--pdf"
              :actions="getPdfActions(item)"
              @select="runPdfAction($event, item)"
            />

            <v-tooltip bottom :content-class="getPublishTooltipClass(item)">
              <template #activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <rmr-action-button
                    label="Publier"
                    :icon="icons.mdiSend"
                    variant="transparent"
                    color="#1E88E5"
                    size="xs"
                    class="gp-table__action-button gp-table__action-button--publish"
                    :loading="publishingBrandId === item.id"
                    :disabled="!canPublish(item) || isPresentationProcessingBusy || publishingBrandId !== null"
                    @click="$emit('publish', item)"
                  />
                </span>
              </template>
              <span>{{ getPublishTooltip(item) }}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  mdiTag,
  mdiHistory,
  mdiFilePowerpoint,
  mdiFilePdfBox,
  mdiSend,
  mdiCloudUpload,
} from "@mdi/js";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrActionMenu from "@/components/ui/actions/RmrActionMenu.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrTableHeader from "@/components/ui/layout/RmrTableHeader.vue";
import { RMR_CA_DARK, RMR_CA_SOFT } from "@/styles/tokens";

export default {
  name: "GeneratePresentationsTableCard",
  components: {
    RmrActionButton,
    RmrActionMenu,
    RmrStatusChip,
    RmrTextField,
    RmrTableHeader,
  },
  props: {
    brands: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: "",
    },
    generatingPptBrandId: {
      type: [Number, String, null],
      default: null,
    },
    generatingPdfBrandId: {
      type: [Number, String, null],
      default: null,
    },
    publishingBrandId: {
      type: [Number, String, null],
      default: null,
    },
    successColor: {
      type: String,
      default: "",
    },
    getPublishTooltip: {
      type: Function,
      required: true,
    },
    getPublishTooltipClass: {
      type: Function,
      required: true,
    },
  },
  emits: [
    "update:search",
    "show-history",
    "generate",
    "convert-pdf",
    "open-pptx-override",
    "open-pdf-override",
    "publish",
  ],
  computed: {
    hasActiveFilters() {
      return Boolean((this.search || "").trim());
    },
    isPresentationProcessingBusy() {
      return this.generatingPptBrandId !== null || this.generatingPdfBrandId !== null;
    },
    icons() {
      return {
        mdiTag,
        mdiHistory,
        mdiFilePowerpoint,
        mdiFilePdfBox,
        mdiSend,
        mdiCloudUpload,
      };
    },
    missingReportChipStyle() {
      return {
        backgroundColor: RMR_CA_SOFT,
        color: RMR_CA_DARK,
      };
    },
  },
  methods: {
    hasPdfOverride(item) {
      return Boolean(item?.has_pdf_override_fr || item?.has_pdf_override_en);
    },
    hasAnyAvailablePdf(item) {
      return Boolean(item?.has_pdf || this.hasPdfOverride(item));
    },
    canPublish(item) {
      return this.hasAnyAvailablePdf(item) && item?.has_report_data;
    },
    getMissingReportTooltip(item) {
      return "Vous ne pouvez pas publier sans donnée rapport.";
    },
    getPptActions(item) {
      const generatePptDisabled = !item.template_number || this.isPresentationProcessingBusy;
      const hasPptxOverride = Boolean(item?.has_pptx_override_fr || item?.has_pptx_override_en);
      return [
        {
          key: "generate-ppt",
          label: "Générer PPT",
          icon: this.icons.mdiFilePowerpoint,
          color: generatePptDisabled ? null : "#F59E0B",
          badgeBackground: generatePptDisabled ? null : "#FFFBEB",
          disabled: generatePptDisabled,
        },
        {
          key: "manual-pptx",
          label: hasPptxOverride ? "Gérer le PPTX manuel" : "Uploader un PPTX manuel",
          icon: this.icons.mdiCloudUpload,
          color: hasPptxOverride ? this.successColor : "#4F46E5",
          badgeBackground: hasPptxOverride ? "#ECFDF5" : "#EEF2FF",
          disabled: false,
        },
      ];
    },
    getPdfActions(item) {
      const generatePdfDisabled = !item.has_pptx || this.isPresentationProcessingBusy;
      const hasPdfOverride = this.hasPdfOverride(item);
      return [
        {
          key: "generate-pdf",
          label: "Générer PDF",
          icon: this.icons.mdiFilePdfBox,
          color: generatePdfDisabled ? null : "#DC2626",
          badgeBackground: generatePdfDisabled ? null : "#FEF2F2",
          disabled: generatePdfDisabled,
        },
        {
          key: "manual-pdf",
          label: hasPdfOverride ? "Gerer la version manuelle" : "Telecharger une version manuelle",
          icon: this.icons.mdiCloudUpload,
          color: hasPdfOverride ? this.successColor : "#4F46E5",
          badgeBackground: hasPdfOverride ? "#ECFDF5" : "#EEF2FF",
          disabled: false,
        },
      ];
    },
    runPptAction(action, item) {
      if (action.disabled) return;
      if (action.key === "generate-ppt") {
        this.$emit("generate", item);
        return;
      }
      if (action.key === "manual-pptx") {
        this.$emit("open-pptx-override", item);
      }
    },
    runPdfAction(action, item) {
      if (action.disabled) return;
      if (action.key === "generate-pdf") {
        this.$emit("convert-pdf", item);
        return;
      }
      if (action.key === "manual-pdf") {
        this.$emit("open-pdf-override", item);
      }
    },
  },
};
</script>
